import { logger } from '@/logger'
import * as persisted from '@/state/persisted'

interface CachedSignedUrl {
  signedUrl: string
  expiration: number
  version: number // Add versioning for cache invalidation
}

interface SignedUrlsCache {
  [path: string]: CachedSignedUrl
}

/**
 * Enhanced signed URL cache with version-based invalidation
 */
class SignedUrlCache {
  private static instance: SignedUrlCache
  private cache: SignedUrlsCache = {}
  private readonly CACHE_KEY = 'signedUrls'

  static getInstance(): SignedUrlCache {
    if (!SignedUrlCache.instance) {
      SignedUrlCache.instance = new SignedUrlCache()
    }
    return SignedUrlCache.instance
  }

  constructor() {
    this.loadCache()
  }

  private loadCache() {
    try {
      const cachedData = persisted.get(this.CACHE_KEY) || {}
      if (typeof cachedData === 'object' && cachedData !== null) {
        this.cache = cachedData as SignedUrlsCache
        logger.info('Loaded signed URLs cache:', {
          cacheSize: Object.keys(this.cache).length,
        })
      } else {
        logger.warn('Invalid signed URLs cache format, resetting cache')
        this.cache = {}
      }
    } catch (error) {
      logger.error('Failed to load signed URLs cache:', {
        errorMessage: error,
      })
      this.cache = {}
    }
  }

  private saveCache() {
    try {
      persisted.write(this.CACHE_KEY, this.cache)
    } catch (error) {
      logger.error('Failed to save signed URLs cache:', {
        errorMessage: error,
      })
    }
  }

  /**
   * Get cached signed URL if valid
   */
  get(path: string, currentVersion: number = 1): string | null {
    const entry = this.cache[path]
    if (!entry) return null

    const now = Date.now()

    // Check if expired or version is outdated
    if (entry.expiration <= now || entry.version < currentVersion) {
      delete this.cache[path]
      this.saveCache()
      return null
    }

    return entry.signedUrl
  }

  /**
   * Set signed URL in cache with version
   */
  set(path: string, signedUrl: string, expiresIn: number, version: number = 1) {
    const now = Date.now()
    this.cache[path] = {
      signedUrl,
      expiration: now + expiresIn * 1000,
      version,
    }
    this.saveCache()
  }

  /**
   * Invalidate cache for specific paths (useful when photos change)
   */
  invalidate(paths: string | string[]) {
    const pathsArray = Array.isArray(paths) ? paths : [paths]

    pathsArray.forEach((path) => {
      delete this.cache[path]
    })

    this.saveCache()
    logger.info('Invalidated signed URL cache for paths:', {
      paths: pathsArray,
    })
  }

  /**
   * Increment version for paths (alternative to deletion)
   */
  incrementVersion(paths: string | string[]) {
    const pathsArray = Array.isArray(paths) ? paths : [paths]

    pathsArray.forEach((path) => {
      if (this.cache[path]) {
        this.cache[path].version += 1
      }
    })

    this.saveCache()
    logger.info('Incremented version for signed URL cache paths:', {
      paths: pathsArray,
    })
  }

  /**
   * Clear all cached URLs
   */
  clear() {
    this.cache = {}
    this.saveCache()
    logger.info('Cleared all signed URLs cache')
  }

  /**
   * Get cache statistics
   */
  getStats() {
    const now = Date.now()
    const entries = Object.entries(this.cache)
    const valid = entries.filter(([, entry]) => entry.expiration > now)
    const expired = entries.length - valid.length

    return {
      total: entries.length,
      valid: valid.length,
      expired,
    }
  }
}

export const signedUrlCache = SignedUrlCache.getInstance()
